import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, MessageCircle, ArrowRight } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import CTASection from '../components/CTASection';
import { SEOHead, createFAQSchema, createBreadcrumbSchema } from '../components/SEOHead';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    {
      name: 'Základní informace a start',
      faqs: [
        {
          q: 'Co je Školobot a pro koho je určen?',
          a: 'Školobot je komplexní AI platforma vytvořená specificky pro české pedagogy a školy. Pomáhá učitelům ušetřit čas při přípravě výukových materiálů - od příprav na hodiny, přes testy až po IVP. Je vhodný pro všechny stupně vzdělávání - mateřské školy, základní i střední školy. Nabízíme jak individuální licence pro jednotlivé učitele, tak komplexní řešení pro celé školy s týmovou spoluprácí.'
        },
        {
          q: 'Jak rychle můžu začít používat Školobot?',
          a: 'Začít můžete prakticky ihned. Registrace zabere jen chvilku a po zakoupení licence získáte okamžitý přístup ke všem funkcím. První materiály můžete začít vytvářet během několika minut od přihlášení. Školobot je navržený tak, aby byl přehledný, intuitivní a plně v češtině, takže se v něm rychle zorientujete i bez složitého zaškolování.'
        },
        {
          q: 'Je Školobot zcela v češtině?',
          a: 'Ano. Celá platforma je plně v češtině – uživatelské rozhraní, všechny šablony, AI asistenti i podpora. Umělá inteligence velmi dobře rozumí češtině, včetně pedagogické terminologie a běžně používaných zkratek, jako jsou RVP, IVP, ŠVP nebo OMJ. Nevytváří texty, které působí jako strojový překlad. Výstupy znějí přirozeně, srozumitelně a profesionálně česky.'
        },
        {
          q: 'Potřebuji něco instalovat?',
          a: 'Ne, Školobot funguje přímo ve webovém prohlížeči. Stačí připojení k internetu a můžete začít pracovat.'
        },
        {
          q: 'Mohu si Školobot prohlédnout před koupí?',
          a: 'Ano. Školobot si můžete prohlédnout na bezplatném webináři nebo si rovnou požádat o zkušební verzi zdarma. Na webináři uvidíte, jak fungují jednotlivé části systému v praxi, a díky trial verzi si můžete Školobot sami vyzkoušet přímo ve své škole. Stačí se přihlásit na nejbližší webinář nebo kliknout na tlačítko Vyzkoušet zdarma.'
        }
      ]
    },
    {
      name: 'Funkce a možnosti',
      faqs: [
        {
          q: 'Jaké typy materiálů může Školobot vytvářet?',
          a: 'Školobot pomáhá s výukou i administrativou. Vytvářet v něm můžete například přípravy na hodiny, testy a kvízy včetně řešení, pracovní listy s klíčem, IVP, slovní hodnocení žáků, tematické plány, materiály k exkurzím nebo prezentace pro žáky. Vedle výukových materiálů pomáhá také s běžnou školní a pedagogickou administrativou. Přitom pracuje v kontextu českého školství a zohledňuje RVP 2025, doporučení MŠMT, materiály NPI i očekávání ČŠI. Podporuje všechny předměty a všechny stupně vzdělávání od MŠ až po SŠ.'
        },
        {
          q: 'Jak funguje AI detektor?',
          a: 'AI detektor analyzuje text a určuje pravděpodobnost, že byl generován umělou inteligencí (ChatGPT, Gemini, Claude, atd.). Používá pokročilé algoritmy a má úspěšnost 85-95% podle typu textu. Funguje skvěle na delší texty (slohové práce, referáty). Výstup je procentuální hodnocení s barevným zvýrazněním podezřelých částí textu. Podporuje češtinu a další jazyky. Používejte jako pomocný nástroj, ne jako jediný důkaz - vždy zvažujte kontext.'
        },
        {
          q: 'Mohu sdílet materiály s kolegy?',
          a: 'Ano. U školní licence můžete materiály sdílet bez omezení. K dispozici máte tři způsoby sdílení: přes integrovanou společnou školní knihovnu, pomocí odkazu nebo exportem do formátu PDF či DOCX. Díky sdílené knihovně mohou pedagogové v rámci školy pracovat se stejnými materiály a postupně budovat společnou databázi výukových podkladů pro celý sbor.'
        },
        {
          q: 'Mohu ve Školobotu vytvářet materiály i v jiných jazycích než v češtině?',
          a: 'Ano. Školobot podporuje více než 50 jazyků. Patří mezi ně všechny běžné evropské jazyky, například angličtina, němčina, francouzština nebo španělština, dále slovanské jazyky, jako je slovenština, polština, ukrajinština či ruština, a také vybrané asijské jazyky, například čínština, japonština nebo korejština. Materiály můžete vytvářet přímo v cizím jazyce, překládat stávající české materiály nebo připravovat dvojjazyčné výstupy pro žáky s OMJ. Tato funkce je užitečná například pro pedagogy jazykových předmětů nebo pro školy, které pracují s mezinárodními programy.'
        },
        {
          q: 'Mohu nahrát vlastní dokumenty a nechat s nimi AI pracovat?',
          a: 'Ano. Do Školobotu můžete nahrávat soubory ve formátech PDF, DOCX, TXT, obrázky, screenshoty i tabulky ve formátu Excel nebo CSV. AI pak může z vašich podkladů vytvářet nové materiály, shrnovat delší dokumenty, aktualizovat starší materiály podle nového RVP, vytahovat klíčové informace nebo překládat celé dokumenty. Maximální velikost jednoho souboru je 50 MB. Data z nahraných dokumentů se nepoužívají k trénování AI modelů.'
        },
        {
          q: 'Podporuje Školobot tvorbu materiálů pro žáky se speciálními vzdělávacími potřebami?',
          a: 'Ano. Školobot umí přizpůsobovat materiály různým vzdělávacím potřebám žáků – například pro žáky s ADHD, dyslexií, OMJ, poruchou autistického spektra nebo pro nadané žáky. Pomáhá vytvářet přehlednější a lépe strukturované úkoly, zjednodušené formulace, dvojjazyčné podklady i rozšiřující zadání pro pokročilejší žáky. Využít ho můžete také při tvorbě IVP, kde zohledňuje konkrétní potřeby žáka a pomáhá s návrhem vhodných opatření.'
        },
        {
          q: 'Potřebuji být technicky zdatný?',
          a: 'Ne. Školobot jsme navrhli tak, aby byl co nejjednodušší a zvládl ho používat i ten, kdo není technický typ. Stačí umět běžně pracovat s internetovým prohlížečem. Celé prostředí je v češtině, je přehledné a doplněné srozumitelnými nápovědami. Aby pro vás začátek byl co nejjednodušší, nabízíme také webináře a vysvětlující videa, ve kterých vše ukazujeme krok za krokem.'
        },
        {
          q: 'Funguje Školobot offline?',
          a: 'Ne. Školobot pro své fungování vyžaduje připojení k internetu, protože pracuje s cloudovými AI modely. To má ale i své výhody: vždy používáte aktuální verzi, data jsou průběžně zálohována a není potřeba nic instalovat ani stahovat žádné aktualizace. Hotové materiály si však můžete stáhnout a následně s nimi pracovat i offline.'
        }
      ]
    },
    {
      name: 'Ceny, platba a tarify',
      faqs: [
        {
          q: 'Kolik Školobot stojí?',
          a: 'Školobot nabízíme ve dvou variantách. Individuální licence stojí 399 Kč měsíčně za uživatele a školní licence 299 Kč měsíčně za pedagoga. Licence se v obou případech hradí na 12 měsíců dopředu, a to na základě faktury převodem na účet.'
        },
        {
          q: 'Jaké způsoby platby jsou k dispozici?',
          a: 'Platbu je možné provést výhradně bankovním převodem. Po dokončení objednávky vám vystavíme fakturu se splatností 14 dní. Fakturu obdržíte e-mailem. Platba kartou ani prostřednictvím platební brány není k dispozici.'
        },
        {
          q: 'Na jak dlouho se předplatné uzavírá?',
          a: 'Předplatné se sjednává vždy na 12 měsíců. Po uplynutí tohoto období vám zašleme fakturu na další období. Pokud nebudete chtít ve využívání Školobota pokračovat, fakturu hradit nemusíte — stačí nám napsat e-mail, že o pokračování nemáte zájem.'
        },
        {
          q: 'Nabízíte zkušební období zdarma?',
          a: 'Ano, nabízíme bezplatnou zkušební verzi. Dostanete balíček kreditů, díky kterému si můžete vyzkoušet všechny funkce Školobotu. Kredity lze využít bez omezení — po jejich vyčerpání se automaticky nedoplňují. Počet zkušebních licencí přizpůsobíme podle vašich potřeb. Stačí se nám ozvat přes kontaktní formulář a vše s vámi nastavíme.'
        },
        {
          q: 'Můžeme jako škola platit fakturou?',
          a: 'Ano, platba na fakturu je u nás běžná. Školám vystavujeme fakturu se splatností 14 dní a doklad obsahuje všechny obvyklé náležitosti potřebné pro zpracování v účetnictví školy. Úhrada probíhá bankovním převodem.'
        },
        {
          q: 'Jaký je rozdíl mezi individuální a školní licencí?',
          a: 'Funkce zůstávají stejné, školní licence je ale výhodnější pro větší tým. Cena je 299 Kč za pedagoga měsíčně místo 399 Kč u individuální licence. Součástí školní licence je navíc jedna společná faktura, sdílená knihovna a možnost správy uživatelů a přístupů.'
        },
        {
          q: 'Jak fungují kredity ve Školobotu?',
          a: 'Školobot funguje na principu kreditního balíčku, který se postupně využívá podle toho, jak intenzivně je používán a kolik materiálů v něm vzniká. U škol jde o společný balíček pro více uživatelů, u individuálních licencí o balíček navázaný na jeden účet. V praxi jsou kredity nastavené tak, aby pro běžné používání bez problémů stačily. Součástí Školobotu je navíc přehled spotřeby, takže uživatel nebo škola vždy vidí, jak jsou kredity využívány.'
        },
        {
          q: 'Jak funguje zkušební verze zdarma?',
          a: 'Školobot si můžete zdarma vyzkoušet přímo ve své škole. Trial verze obsahuje plnou funkcionalitu, takže si můžete ověřit, jak funguje v běžné školní praxi — na vlastních školních datech a při každodenní práci pedagogů, ne jen na ukázkových příkladech. Díky tomu uvidíte, jak se Školobot chová v reálném prostředí české školy a jak přesně odpovídá potřebám českého pedagoga. Zkušební verze zahrnuje balíček kreditů, které lze využít bez omezení. Po vyčerpání se kredity automaticky nedoplňují. Na vyžádání umíme zpřístupnit i více zkušebních licencí, aby si Školobot mohlo vyzkoušet více pedagogů najednou. Školobot je zároveň koncipovaný v souladu s metodickým výkladem MŠMT „Digitální technologie jako učební pomůcka", takže jeho pořízení lze za splnění podmínek metodiky hradit z veřejných prostředků jako učební pomůcku podle § 160 školského zákona.'
        }
      ]
    },
    {
      name: 'Bezpečnost, GDPR a data',
      faqs: [
        {
          q: 'Jsou mé dokumenty a data v bezpečí?',
          a: 'Ano, bezpečnost vašich dat je pro nás zásadní. Data ukládáme v šifrované podobě, komunikace probíhá přes zabezpečené SSL/TLS připojení a servery provozujeme v EU. Samozřejmostí jsou pravidelné zálohy a auditní záznamy, díky kterým máte přehled o tom, kdo a kdy k systému přistupoval. Každá škola vidí pouze své vlastní materiály a přístupy jsou nastavené tak, aby se k nim nikdo nepovolaný nedostal. Při zpracování dat dbáme na soulad s požadavky GDPR. Vaše dokumenty ani výstupy pedagogů nikdy nepoužíváme k trénování AI modelů.'
        },
        {
          q: 'Kdo má přístup k mým dokumentům a materiálům?',
          a: 'Pouze vy a lidé, kterým přístup sami udělíte, například kolegové ve vašem týmu. Náš technický tým nemá běžný přístup k obsahu vašich dokumentů a pro účely podpory pracuje pouze s nezbytnými technickými informacemi, například s údaji o počtu souborů nebo stavu účtu. Vaše dokumenty nikdy neprohlížíme, nepředáváme ani neprodáváme třetím stranám. Každá škola má navíc své oddělené prostředí, takže data jedné školy nejsou přístupná jiné škole.'
        },
        {
          q: 'Je Školobot v souladu s GDPR?',
          a: 'Ano, ochranu osobních údajů bereme vážně a při provozu Školobota dbáme na soulad s požadavky GDPR. Na našem webu najdete Zásady ochrany osobních údajů a Dodatek o zpracování osobních údajů (DPA). Data chráníme pomocí technických a organizačních opatření a podporujeme i práva subjektů údajů, například opravu, výmaz nebo přenositelnost dat. Školám navíc poskytujeme potřebné podklady pro kontrolu nebo audit. Data ukládáme na serverech v EU, konkrétně v Německu.'
        },
        {
          q: 'Co se stane s daty po zrušení účtu?',
          a: 'Po ukončení používání účtu uchováváme data ještě 30 dní v režimu pouze pro čtení. Během této doby si můžete stáhnout své materiály, obnovit účet bez ztráty dat nebo do dokumentů dál nahlížet. Po 30 dnech jsou data trvale smazána z našich serverů i ze záloh. Na požádání vám vystavíme potvrzení o smazání dat.'
        },
        {
          q: 'Používáte mé dokumenty k tréninku AI?',
          a: 'Ne. Vaše dokumenty, přípravy, testy ani další materiály nepoužíváme k trénování AI modelů. Obsah, který do Školobotu nahrajete nebo v něm vytvoříte, zůstává váš. Při využití AI pracujeme přes API režim, který vaše data nevyužívá k dalšímu tréninku modelů. Vaše materiály nesdílíme ani s jinými školami, ani je neposkytujeme třetím stranám k učení AI.'
        },
        {
          q: 'Mohu si stáhnout svá data?',
          a: 'Ano. Svá data a materiály si můžete kdykoliv exportovat. Podporujeme export jednotlivých dokumentů v PDF a DOCX, takže se ke svým materiálům dostanete i mimo Školobot.'
        }
      ]
    },
    {
      name: 'Podpora a školení',
      faqs: [
        {
          q: 'Jak se mohou pedagogové naučit pracovat se Školobotem?',
          a: 'Pedagogům nabízíme několik možností, jak se Školobot naučit používat. Samotné prostředí je velmi intuitivní a navržené tak, aby se v něm pedagogové rychle zorientovali i bez složitého zaškolování. K dispozici jsou video návody, díky kterým mohou začít vlastním tempem. Každý týden také pořádáme webinář zdarma, kde ukazujeme praktické využití Školobota a odpovídáme na dotazy. Pro školy umíme připravit i školení na míru, a to od 5 účastníků výše.'
        },
      ]
    },
    {
      name: 'Školní tarify a týmy',
      faqs: [
        {
          q: 'Můžeme jako škola začít s menším počtem učitelů?',
          a: 'Ano. Můžete začít s menším počtem licencí a další kdykoliv dokoupit. Pokud licence přidáte v průběhu již běžícího období, první platba za nové licence se vypočítá poměrně jen do konce období, ve kterém byla zakoupena vaše první licence.'
        },

      ]
    },
    {
      name: 'Srovnání s konkurencí',
      faqs: [
        {
          q: 'Čím se lišíte od ChatGPT nebo jiných obecných AI nástrojů?',
          a: 'Školobot není obecný AI nástroj, ale řešení navržené přímo pro české školy a pedagogy. Rozumí českému školskému prostředí, pracuje s pedagogickou terminologií a zohledňuje aktuální rámce, jako je RVP. Učitelům navíc nabízí hotové šablony a praktické nástroje, takže nemusejí složitě vymýšlet prompty od nuly. Vše je v češtině, přehledně na jednom místě, včetně knihovny, sdílení a dalších funkcí pro školní praxi. Důležitý je i důraz na bezpečnost dat, provoz v EU a podporu v češtině od skutečných lidí. Zatímco obecné AI nástroje jsou univerzální, Školobot je specificky vyvinutý pro české školy.'
        }
      ]
    }
  ];

  // Filter FAQs based on search
  const filteredCategories = categories.map(cat => ({
    ...cat,
    faqs: cat.faqs.filter(faq => 
      faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.faqs.length > 0);

  // Build FAQ schema for AI crawlers
  const allFaqs = useMemo(() => categories.flatMap(cat => cat.faqs), [categories]);
  const faqSchema = useMemo(() => createFAQSchema(allFaqs), [allFaqs]);
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Úvod', path: '/' },
    { name: 'FAQ', path: '/faq' }
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[color:var(--bg)] to-white">
      <SEOHead 
        title="Často kladené otázky | Školobot"
        description="Odpovědi na nejčastější otázky o Školobotovi - AI nástroji pro české pedagogy. Ceny, funkce, bezpečnost, GDPR a další."
        path="/faq"
        schema={[faqSchema, breadcrumbSchema]}
      />
      {/* Hero */}
      <section className="pt-28 pb-8 md:pt-32 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <FadeIn>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[color:var(--text-primary)] mb-3">
              Často kladené otázky
            </h1>
            <p className="text-base md:text-lg text-[color:var(--text-secondary)] max-w-2xl mx-auto mb-6">
              Najděte odpovědi na nejčastější otázky. Nenašli jste? Kontaktujte nás!
            </p>

            {/* Search - Compact */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[color:var(--text-secondary)]" />
                <input
                  type="text"
                  placeholder="Hledat... (např. 'cena', 'GDPR', 'videa')"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-[color:var(--primary-200)] rounded-xl focus:outline-none focus:border-[color:var(--primary-500)]"
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Sections - Compact */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-8">
          {(searchTerm ? filteredCategories : categories).map((category, catIndex) => (
            <FadeIn key={catIndex} delay={0}>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[color:var(--text-primary)] mb-4 flex items-center gap-2">
                  {category.name}
                  <span className="text-sm font-normal text-[color:var(--text-secondary)]">({category.faqs.length})</span>
                </h2>
                <div className="space-y-3">
                  {category.faqs.map((faq, faqIndex) => {
                    const globalIndex = `${catIndex}-${faqIndex}`;
                    const isOpen = openIndex === globalIndex;
                    
                    return (
                      <div 
                        key={faqIndex}
                        className="bg-white border-2 border-[color:var(--primary-100)] rounded-xl overflow-hidden transition-all duration-300 hover:border-[color:var(--primary-300)] hover:shadow-md"
                      >
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                          className="w-full text-left p-4 flex items-center justify-between gap-4 hover:bg-[color:var(--primary-50)] transition-colors"
                        >
                          <h3 className="text-base font-bold text-[color:var(--text-primary)] pr-4">
                            {faq.q}
                          </h3>
                          <ChevronDown 
                            className={`w-5 h-5 text-[color:var(--primary-500)] flex-shrink-0 transition-transform duration-300 ${
                              isOpen ? 'transform rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div 
                          className={`overflow-hidden transition-all duration-300 ${
                            isOpen ? 'max-h-[800px]' : 'max-h-0'
                          }`}
                        >
                          <div className="px-4 pb-4 border-t border-[color:var(--border)]">
                            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed mt-3 whitespace-pre-line">
                              {faq.a}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </FadeIn>
          ))}

          {searchTerm && filteredCategories.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-[color:var(--text-secondary)] mb-4">
                Nenašli jsme žádné otázky odpovídající "{searchTerm}"
              </p>
              <button 
                onClick={() => setSearchTerm('')}
                className="text-[color:var(--primary-600)] hover:underline"
              >
                Vymazat hledání
              </button>
            </div>
          )}
        </div>
      </section>



      <CTASection />
    </div>
  );
};

export default FAQPage;